import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { ProgressBar } from '../../molecules/progressBar/ProgressBar'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { BodyText } from '../bodyText/BodyText'
import { ArrowIcon, ArrowIconDirection } from '../icons/ArrowIcon'
import { LoadingSkeleton } from '../loadingSkeleton/LoadingSkeleton'
import { Tooltip } from '../tooltip/Tooltip'

export interface SupervisorSkillsCardRowProps {
  value: string
  label: string
  averageScore: number
  arrowDirection?: ArrowIconDirection
  progress?: number
  isLoading?: boolean
}

const SupervisorSkillsCardRowContainer = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const SkillLabel = styled.View({
  flex: 1,
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
})

const ProgressBarContainer = styled.View({
  width: '136px',
})

export const SKILLS_PROGRESS_EXCEPTIONAL_THRESHOLD = 2.59
export const SKILLS_PROGRESS_EFFECTIVE_THRESHOLD = 1.89

export const SupervisorSkillsCardRow: FunctionComponent<SupervisorSkillsCardRowProps> = ({
  value,
  label,
  averageScore,
  arrowDirection,
  progress,
  isLoading = false,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme() as ThemeType
  const componentColors = colors.components.supervisorSkillsCard

  const getProgressBarColor = (score: number) => {
    if (score < SKILLS_PROGRESS_EFFECTIVE_THRESHOLD) {
      return componentColors.progressBar.opportunity.background
    }
    if (score < SKILLS_PROGRESS_EXCEPTIONAL_THRESHOLD) {
      return componentColors.progressBar.effective.background
    }
    return componentColors.progressBar.exceptional.background
  }

  const arrowTooltipText =
    arrowDirection && progress !== undefined
      ? formatMessage(
          {
            defaultMessage: '{isPositiveProgress, select, true {Up} other {Down}} by {progress} since last month',
            description: 'Tooltip showing score change since last month',
          },
          {
            isPositiveProgress: progress > 0,
            progress: Math.abs(progress).toFixed(1).replace(/^0\./, '.'),
          },
        )
      : undefined

  const progressBarTooltipText = formatMessage(
    {
      defaultMessage: '{score} / 3',
      description: 'Tooltip showing the average score out of 3',
    },
    { score: averageScore.toFixed(1) },
  )

  if (isLoading) {
    return (
      <SupervisorSkillsCardRowContainer testID={tID(`SupervisorSkillsCardRow-${value}`)}>
        <LoadingSkeleton
          width={250}
          maxWidth='50%'
          height={16}
          marginTop={4}
          testID={tID('SupervisorSkillsCardRow-valueLoadingSkeleton')}
        />
        <LoadingSkeleton
          width={136}
          maxWidth='50%'
          height={16}
          marginTop={4}
          testID={tID('SupervisorSkillsCardRow-progressBarLoadingSkeleton')}
        />
      </SupervisorSkillsCardRowContainer>
    )
  }

  return (
    <SupervisorSkillsCardRowContainer testID={tID(`SupervisorSkillsCardRow-${value}`)}>
      <SkillLabel>
        <BodyText
          text={label.charAt(0).toUpperCase() + label.slice(1)}
          size={BodyTextSize.SMALL}
          color={colors.textSecondary}
        />
        {arrowDirection && progress !== undefined && (
          <Tooltip
            content={arrowTooltipText}
            placement='top'
            hoverEnabled
            accessibilityLabel={arrowTooltipText}
            testID={'arrow'}
          >
            <ArrowIcon
              direction={arrowDirection}
              size={16}
              fillColor={colors.iconDefault}
              testID={tID(`SupervisorSkillsCardRow-${value}-trendArrow-${arrowDirection}`)}
            />
          </Tooltip>
        )}
      </SkillLabel>
      <ProgressBarContainer testID={tID(`SupervisorSkillsCardRow-${value}-progressBar`)}>
        <Tooltip
          content={progressBarTooltipText}
          placement='top'
          hoverEnabled
          accessibilityLabel={progressBarTooltipText}
          testID={'progressBar'}
        >
          <ProgressBar
            progress={averageScore / 3}
            height={8}
            color={getProgressBarColor(averageScore)}
            backgroundColor={componentColors.progressBar.background.background}
            borderRadius={4}
            width={136}
          />
        </Tooltip>
      </ProgressBarContainer>
    </SupervisorSkillsCardRowContainer>
  )
}
